import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SyncRunModel } from '@/models/sharepoint';
import { LibraryService } from '@/services/library.service';
import Button from '@/components/Button';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './sharepoint-modal.module.scss';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

type Props = {
  syncRuns: SyncRunModel[];
  initialRunId: string | null;
  orgID?: string;
};

const RunDetailsTab = ({ syncRuns, initialRunId, orgID }: Props) => {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    initialRunId ?? syncRuns[0]?.mongodb_document_id ?? null
  );

  useEffect(() => {
    if (initialRunId) setSelectedRunId(initialRunId);
  }, [initialRunId]);

  const {
    data: run,
    isFetching,
    refetch,
  } = useQuery<SyncRunModel>({
    queryKey: ['sharepoint-run-detail', selectedRunId, orgID],
    queryFn: () =>
      LibraryService.getSharepointSyncRunStatus(selectedRunId!, orgID),
    enabled: !!selectedRunId,
    staleTime: 1000 * 60 * 2,
  });

  const isInProgress = run?.status === 'in_progress';

  return (
    <div className={styles['run-details']}>
      <div className={styles['run-details-header']}>
        <select
          className={styles['run-select']}
          value={selectedRunId ?? ''}
          onChange={(e) => setSelectedRunId(e.target.value || null)}
        >
          {syncRuns.length === 0 && <option value="">No runs available</option>}
          {syncRuns.map((r) => (
            <option key={r.mongodb_document_id} value={r.mongodb_document_id}>
              {new Date(r.timestamp).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              — {r.status}
            </option>
          ))}
        </select>

        {isInProgress && (
          <Button onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        )}
      </div>

      {!selectedRunId || syncRuns.length === 0 ? (
        <p className={styles['empty-state']}>No sync runs available.</p>
      ) : isFetching && !run ? (
        <div className={styles['run-loading']}>
          <LoadingSpinner />
          <p className={styles['run-loading__text']}>Loading run details…</p>
        </div>
      ) : run ? (
        <>
          <div className={styles['run-meta']}>
            <span className={styles['status-badge']} data-status={run.status}>
              {run.status}
            </span>
            <span className={styles['run-meta__separator']} />
            <span className={styles['run-meta__duration']}>
              {formatDuration(run.duration_seconds)}
            </span>
          </div>

          <div className={styles['stats-grid']}>
            <div className={styles['stat-card']}>
              <span className={styles['stat-label']}>Files Seen</span>
              <span className={styles['stat-value']}>{run.files_seen}</span>
            </div>
            <div className={styles['stat-card']}>
              <span className={styles['stat-label']}>Files Skipped</span>
              <span className={styles['stat-value']}>{run.files_skipped}</span>
            </div>
            <div className={styles['stat-card']}>
              <span className={styles['stat-label']}>Downloaded</span>
              <span className={styles['stat-value']}>
                {run.files_downloaded}
              </span>
            </div>
            <div className={styles['stat-card']}>
              <span className={styles['stat-label']}>Errors</span>
              <span
                className={styles['stat-value']}
                data-error={run.errors > 0 ? 'true' : 'false'}
              >
                {run.errors}
              </span>
            </div>
          </div>

          {run.errors > 0 && (
            <div className={styles['error-banner']}>
              <span className={styles['error-banner__icon']}>⚠</span>
              <span>{run.error_message ?? 'No error details found.'}</span>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default RunDetailsTab;
